import { useState } from 'react';
import { Button } from '@/components/Button';
import { Plus } from 'lucide-react';

interface AddNoteFormProps {
  onSubmit: (content: string, isPinned: boolean) => Promise<void>;
  loading?: boolean;
}

export function AddNoteForm({ onSubmit, loading }: AddNoteFormProps) {
  const [content, setContent] = useState('');
  const [isPinned, setIsPinned] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!content.trim()) return;

    await onSubmit(content.trim(), isPinned);
    setContent('');
    setIsPinned(false);
    setIsExpanded(false);
  };

  if (!isExpanded) {
    return (
      <Button
        size="sm"
        variant="secondary"
        leftIcon={<Plus className="h-4 w-4" />}
        onClick={() => setIsExpanded(true)}
      >
        Add Note
      </Button>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-2">
      <textarea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        placeholder="Add a note..."
        rows={3}
        disabled={loading}
        className="w-full rounded border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-foreground-muted focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:cursor-not-allowed disabled:opacity-50"
        autoFocus
      />
      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 text-sm text-foreground">
          <input
            type="checkbox"
            checked={isPinned}
            onChange={(e) => setIsPinned(e.target.checked)}
            disabled={loading}
            className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
          />
          Pin this note
        </label>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => {
              setContent('');
              setIsPinned(false);
              setIsExpanded(false);
            }}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            size="sm"
            disabled={loading || !content.trim()}
            loading={loading}
          >
            Add Note
          </Button>
        </div>
      </div>
    </form>
  );
}
